import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import ConfirmPaymentRetry from '@features/ConfirmPaymentRetryModal/ui/ConfirmPaymentRetry';
import { BILLING_PATH } from '@shared/constants/paths';
import { runManualPayment } from '@api/user/apiService';
import useToast from '@hooks/useToast';
import { IconAlertCircle } from '@tabler/icons-react';

interface SubscriptionPaymentFailedProps {
  onSuccess: () => void;
}

function SubscriptionPaymentFailed({
  onSuccess,
}: SubscriptionPaymentFailedProps) {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const retryPayment = () => {
    setIsLoading(true);
    runManualPayment(
      () => {
        onSuccess();
        closeConfirmationModal();
        showSuccess('Success', 'Your payment has been processed successfully');
      },
      (error) => {
        console.error(error);
        showError(
          'Error',
          error.response?.data.error || 'Something went wrong'
        );
      },
      () => setIsLoading(false)
    );
  };

  return (
    <Stack
      direction={isSmallScreen ? 'column' : 'row'}
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: theme.palette.error.main,
        borderRadius: 2,
        backgroundColor: theme.palette.error.light,
      }}
    >
      <Stack direction="column" spacing={1} sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: theme.palette.error.light,
            }}
          >
            <IconAlertCircle color={theme.palette.error.main} />
          </Box>

          <Typography variant="h6" color={theme.palette.error.main}>
            Payment Failed
          </Typography>
        </Stack>

        <Typography variant="body2" color={theme.palette.error.main}>
          Hey there. We couldn&#39;t process your payment. Please update your
          payment method to continue using the service.
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2}>
        <Button
          variant="contained"
          color="error"
          onClick={() => navigate(BILLING_PATH)}
        >
          Update Payment Method
        </Button>

        <Button
          variant="outlined"
          color="error"
          onClick={() => setShowConfirmationModal(true)}
        >
          Retry Payment
        </Button>
      </Stack>

      <ConfirmPaymentRetry
        showModal={showConfirmationModal}
        onCancel={closeConfirmationModal}
        onConfirm={retryPayment}
        isLoading={isLoading}
      />
    </Stack>
  );
}

export default SubscriptionPaymentFailed;
